import type { ErrorHandlerOpts } from 'ra-core'
import { useCallback, useEffect, useRef } from 'react'
import { useErrorHandling } from './useErrorHandling'

/**
 * This is a utility to wrap long running processes in a timeout that
 * sends an error to Sentry if either success or failure isn't detected
 * within the timeframe.
 *
 * To use this with a promise-based API:
 * ```js
 * const longErrors = useLongProcessErrorHandler()
 * ...
 * const promise = longErrors.promise(10000, new Error('Calcs timed out'), longCalcsProcess())
 * promise.then(() => {...})
 * promise.catch(() => {...})
 * ```
 *
 * To use this with a callbacks based API:
 * ```js
 * const longErrors = useLongProcessErrorHandler()
 * ...
 * const onSuccess = () => {
 *    //  normal success handler here
 * }
 * const onFailure = () => {
 *    //  normal failure handler here
 * }
 * const [onSuccessWrapped, onFailureWrapped] = longErrors.callbacks(10000, new Error('Calcs timed out'), [onSuccess, onFailure]})
 * longCalcsProcess(onSuccessWrapped, onFailureWrapped)
 * ```
 *
 * Or you can use the core implementation like this:
 * ```js
 * const longErrors = useLongProcessErrorHandler()
 * ...
 * const { complete } = longErrors.core(10000, new Error('Calcs timed out'))
 * ...
 * // When I'm ready to clear the long running timer:
 * complete()
 *
 * ```
 */

export interface LongErrorHandler {
  timerId: NodeJS.Timeout
  complete: () => void
}

export const useLongProcessErrorHandler = () => {
  const errorHandling = useErrorHandling()

  const core = (timeout: number, error: Error, options?: { errorHandlerOts?: ErrorHandlerOpts }): LongErrorHandler => {
    const timerId = setTimeout(() => {
      errorHandling.long_process(error, options?.errorHandlerOts)
    }, timeout)

    const complete = () => {
      clearTimeout(timerId)
    }

    return { timerId, complete }
  }
  const callbacks = (timeout: number, error: Error, callbacks: Function[]) => {
    const ret = core(timeout, error)
    const callbacksWrapped = callbacks.map((func) => {
      return function () {
        ret.complete()
        func.apply(null, arguments)
      }
    })
    return callbacksWrapped
  }

  const promise = (
    timeout: number,
    error: Error,
    promise: Promise<any>,
    options?: { errorHandlerOts?: ErrorHandlerOpts }
  ) => {
    return new Promise((resolve, reject) => {
      const ret = core(timeout, error, options)
      promise
        .then((value) => {
          ret.complete()
          resolve(value)
        })
        .catch((value) => {
          ret.complete()
          reject(value)
        })
    })
  }

  return { callbacks, promise, core }
}

// Simplified version of the core implementation that hooks into the component
// lifecycle. Returns a stable `complete` callback available from the very first
// render (via useCallback + useRef) so callers in child componentDidMount /
// synchronous paths can cancel the timer even before useEffect has fired.
export const useLongProcessError = (timeout: number, error: Error, active = true) => {
  const longErrors = useLongProcessErrorHandler()
  const handlerRef = useRef<LongErrorHandler | null>(null)
  const completedRef = useRef(false)

  const complete = useCallback(() => {
    completedRef.current = true
    handlerRef.current?.complete()
  }, [])

  useEffect(() => {
    if (!active) {
      handlerRef.current = null
      return
    }
    if (completedRef.current) {
      return
    }
    const ret = longErrors.core(timeout, error)
    handlerRef.current = ret
    return () => {
      ret.complete()
      handlerRef.current = null
      completedRef.current = false
    }
  }, [active])

  return active ? complete : undefined
}
